import {
    type MutationFunction,
    type UseMutationOptions,
    type UseMutationResult,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { z } from 'zod';
import { isObject } from '../../../../react-commons/utils/utilFunctions';
import { setMutationInProgress } from '../../ui/ui-state/local-status/localStatusActions';
import { ValidationType } from '../enumerations/ValidationType';
import type { MutationValidationResource } from '../resources/validation/mutationValidationResource';
import { parseRawData } from './adapters/AdapterUtils';

function isMutationValidationResource(object: unknown): object is MutationValidationResource {
    return isObject(object) && 'validationType' in object;
}

export const useMutationWithDelay = <TData = unknown, TError = unknown, TVariables = unknown, TContext = unknown>(
    mutationFn: MutationFunction<TData, TVariables>,
    mutationVariablesSchema: z.ZodSchema, // TODO z.ZodSchema<TVariables>, // won't work because of z.unknown() in lots of mutation schemas
    options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'>,
    deletionInvalidation?: (data: TVariables) => void
): UseMutationResult<TData, TError, TVariables, TContext> => {
    const queryClient = useQueryClient();
    const isSettled = new Uint8Array(1);
    const dispatch = useDispatch();

    const [loadingMutationResult, setLoadingMutationResult] = useState<Promise<TData> | undefined>(undefined);

    Atomics.store(isSettled, 0, 0);

    const mutationResults = useMutation<TData, TError, TVariables, TContext>({
        mutationFn: (variables, context) =>
            mutationFn(parseRawData(variables, mutationVariablesSchema) as TVariables, context),
        ...options,
        onMutate: (variables, context) => {
            if (options?.onMutate) {
                void options.onMutate(variables, context); // TODO await?!
            }
            setTimeout(() => {
                const loaded = Atomics.load(isSettled, 0);
                if (loaded === 0) {
                    dispatch(setMutationInProgress(true));
                }
            }, 300);
            return isSettled as TContext;
        },
        onSettled: (data, error, variables, onMutateResult, context) => {
            Atomics.store(onMutateResult as Uint8Array, 0, 1);
            dispatch(setMutationInProgress(false));
            setLoadingMutationResult(undefined);

            const invalidateQueries = () => {
                if (deletionInvalidation) {
                    deletionInvalidation(variables);
                } else {
                    void queryClient // TODO await?!
                        .invalidateQueries({
                            predicate: (query) => query.queryKey[0] !== 'INFINITY_STALE_TIME',
                            type: 'all',
                            refetchType: 'active',
                        });
                }
            };

            if (data && isMutationValidationResource(data)) {
                if (data.validationType === ValidationType.UPDATE) {
                    if (data.changed) {
                        invalidateQueries();
                    }
                } else if (data.successful) {
                    invalidateQueries();
                }
            } else {
                invalidateQueries();
            }
            if (options?.onSettled) {
                options.onSettled(data, error, variables, onMutateResult, context);
            }
        },
    });

    return useMemo(
        () => ({
            ...mutationResults,
            mutate: (...params: [TVariables]) => {
                if (!mutationResults.isPending) {
                    mutationResults.mutate(...params);
                }
            },
            mutateAsync: (...params: [TVariables]): Promise<TData> => {
                if (!mutationResults.isPending) {
                    const asyncMutation = mutationResults.mutateAsync(...params);
                    setLoadingMutationResult(asyncMutation);
                    return asyncMutation;
                }
                if (loadingMutationResult) {
                    return loadingMutationResult;
                }
                return Promise.reject('');
            },
        }),
        [loadingMutationResult, mutationResults]
    );
};
